// /welcome (docs/08-app-design.md 5.1 step 1 and 5.2 entry;
// app/art/welcome/PLAN.md sections 4.1 and 5): the entrance, in the home
// page's frame. A fresh device gets the two doors, the one status line, the
// two secrets in one sentence, and a preview of the five stops Create then
// walks through. Nothing moves: the picture is EntranceArt's one still.
// - starting: the frame and the still, with an empty reserved column, so a
//   locked device never flashes the fresh doors;
// - unlocked: straight on to /vault;
// - locked: section 4.2, the same DeviceDoor as /locked (unlock first, the
//   shared Forget sheet with the fee reserve's balance, R48), with its own
//   lede; Create and Restore are not offered, since forgetting this device
//   is the way to either. Once the device is forgotten (by the door's
//   FORGET, Try again, or a queued deletion that completes by itself) the
//   fresh page replaces it with no route change, so focus moves to main as
//   a route change would move it.
// - a setup this page load saved that the session could not read back (R67's
//   double fault: the session then reports an empty device): not the fresh
//   doors, since storage holds a setup; the saved screen's reload instead,
//   which starts the session again on /locked (Ceremony.tsx).
// - a save this page load started is committing or being opened (the user
//   left /create or /restore with browser Back during it; savedSetup.ts
//   'saving'): a neutral "Saving your setup" screen, neither the fresh doors
//   nor a failure, until the session holds the setup and this page moves on.
// - newer-build: a later build has upgraded this device's database (the
//   version fence, docs/18-one-fee-reserve.md section 3, writer step 6), so
//   this build can neither tell whether a vault is stored here nor save
//   one. The page says so and offers neither door; the rest stays (App.tsx
//   routes the state to this page).

import { useEffect, useRef } from 'react';
import { Link, Redirect } from 'wouter';
import { SUGGESTED_FUNDING_SOL, SAVED_SETUP_UNREAD, SetupSaved, SetupSaving, useSavedSetup } from '../components/Ceremony';
import { DeviceDoor } from '../components/DeviceDoor';
import { EntranceArt } from '../components/EntranceArt';
import { EntranceFrame, EntranceStatus } from '../components/EntranceFrame';
import { StatusLine } from '../components/primitives';
import { StepRail } from '../components/StepRail';
import { NEWER_BUILD_SENTENCE, useSession } from '../session';

/** One line under each of the five stops (PLAN.md section 5). */
const CREATE_SUBS = ['24 words, on paper.', 'Type three of them back.', 'Opens the vault on this device.', `About ${SUGGESTED_FUNDING_SOL} SOL for fees and rent.`, 'One small transaction.'];

export function Welcome() {
  const { state } = useSession();
  const saved = useSavedSetup();
  const previous = useRef(state);
  useEffect(() => {
    const was = previous.current;
    previous.current = state;
    if (was !== 'locked' || state !== 'no-vault') return;
    const id = requestAnimationFrame(() => document.getElementById('main')?.focus());
    return () => cancelAnimationFrame(id);
  }, [state]);

  if (state === 'unlocked') return <Redirect to="/vault" replace />;
  if (state === 'starting') return <EntranceFrame art={<EntranceArt />} busy />;

  if (saved) {
    return (
      <EntranceFrame width="read" secret>
        <div className="cer">{saved === 'saving' ? <SetupSaving /> : <SetupSaved error={SAVED_SETUP_UNREAD} next="reload" busy={false} />}</div>
      </EntranceFrame>
    );
  }

  if (state === 'locked') {
    return (
      <EntranceFrame art={<EntranceArt size="small" />}>
        <DeviceDoor lede="This device already holds a vault. Unlock it here, or forget this device to create or restore a different one." />
      </EntranceFrame>
    );
  }

  return (
    <EntranceFrame art={<EntranceArt />}>
      <h1>Your vault starts here.</h1>
      <p className="entry-lede one-line">Create a new vault, or restore yours from the 24 words it was made with.</p>
      {state === 'newer-build' ? (
        <StatusLine state="failed" word="Not opened.">
          {NEWER_BUILD_SENTENCE}
        </StatusLine>
      ) : (
        <div className="entry-actions">
          <Link href="/create" className="cta cta-mint">
            Create a vault
          </Link>
          <Link href="/restore" className="cta cta-violet">
            Restore my vault
          </Link>
        </div>
      )}
      <EntranceStatus />
      <p className="secrets">
        <b>Two secrets, two jobs:</b> your 24 words restore the vault anywhere; the device passphrase opens it on this device only.
      </p>
      <section className="takes" aria-labelledby="takes-title">
        <h2 id="takes-title">What creating a vault takes</h2>
        <StepRail flow="create" preview subs={CREATE_SUBS} />
        <p className="need">
          <b>You will need</b> paper and a pen, a place where nobody can see your screen, and about {SUGGESTED_FUNDING_SOL} SOL in another wallet.
        </p>
        <p className="rnote">Restoring needs your 24 words in order and a little SOL if your fee reserve is low, then runs one key refresh. Use one device at a time.</p>
        <ul className="facts">
          <li>No account to create</li>
          <li>No analytics</li>
          <li>Nobody can recover the 24 words for you</li>
        </ul>
      </section>
    </EntranceFrame>
  );
}
